import React from 'react';
import { VendorSummary, PaginationInfo } from './types';

interface VendorListProps {
  vendors: VendorSummary[];
  loading: boolean;
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onViewDetails: (vendor: VendorSummary) => void;
  onEditVendor: (vendor: VendorSummary) => void;
  onDeleteVendor: (vendorId: string) => void;
}

const VendorList: React.FC<VendorListProps> = ({
  vendors,
  loading,
  pagination,
  onPageChange,
  onViewDetails,
  onEditVendor,
  onDeleteVendor
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const { currentPage, totalPages } = pagination;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading vendors...</p>
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <div className="empty-state">
        <h3>No vendors found</h3>
        <p>Get started by adding your first vendor or try adjusting your search criteria.</p>
      </div>
    );
  }

  return (
    <div className="vendor-list">
      <div className="vendor-table-container">
        <table className="vendor-table">
          <thead>
            <tr>
              <th>Vendor ID</th>
              <th>Vendor Name</th>
              <th>Products</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((vendor) => (
              <tr key={vendor._id}>
                <td>
                  <code className="vendor-id">{vendor.vendorId}</code>
                </td>
                <td>
                  <strong>{vendor.vendorName}</strong>
                </td>
                <td>
                  <span className="product-count">
                    {vendor.productCount} product{vendor.productCount !== 1 ? 's' : ''}
                  </span>
                </td>
                <td>
                  <span className="date-text">
                    {formatDate(vendor.createdAt)}
                  </span>
                </td>
                <td>
                  <span className="date-text">
                    {formatDate(vendor.updatedAt)}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => onViewDetails(vendor)}
                      title="View details"
                    >
                      👁️ View
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => onEditVendor(vendor)}
                      title="Edit vendor"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => onDeleteVendor(vendor._id)}
                      title="Delete vendor"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
            {pagination.totalItems} vendors
          </div>
          
          <div className="pagination">
            <button
              className="btn btn-sm btn-outline"
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              « Previous
            </button>

            {generatePageNumbers().map((page) => (
              <button
                key={page}
                className={`btn btn-sm ${
                  page === pagination.currentPage ? 'btn-primary' : 'btn-outline'
                }`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="btn btn-sm btn-outline"
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Next »
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorList;
